import { useMemo } from "react";
import { useData } from "../../hooks/useData";
import { useRevenuePlanner } from "../../hooks/useRevenuePlanner";
import { useDMAnalytics } from "../../hooks/useDMAnalytics";
import { SOURCE_KEYS, type SourceKey, type RateOverrides } from "../../lib/revenuePlannerEngine";
import type { CurrentMonthMetrics } from "../../hooks/useForecastData";
import GoalHeader from "./GoalHeader";
import SourceToggle from "./SourceToggle";
import SourceCard from "./SourceCard";
import ProjectionSummary from "./ProjectionSummary";

// =============================================================================
// RevenuePlanner — Two-Panel Bidirectional Funnel Calculator
// =============================================================================
// PURPOSE: Set a revenue goal, see what daily inputs are needed per source
// (left panel), then adjust sliders to model scenarios (right panel).
//
// LEFT:  "What We Need" — goal → reverse-calc → required leads/day per source
// RIGHT: "What Happens If" — adjust leads/day sliders → projected revenue
//
// Source toggles (webinar/typeform/direct) enable honest scenario modeling:
// toggling OFF a source removes its contribution and shows the real gap.
// =============================================================================

interface RevenuePlannerProps {
  organizationId: string | null | undefined;
  currentMonthMetrics: CurrentMonthMetrics;
  canEdit: boolean; // exec/client can edit, others read-only
}

export default function RevenuePlanner({
  organizationId,
  currentMonthMetrics,
  canEdit,
}: RevenuePlannerProps) {
  // Org timezone for the DM funnel query below. Read from DataContext rather than
  // taken as a prop: the only render site (OperationsDashboard.tsx:850) passes
  // organizationId={currentOrganization?.id}, so context is the same org by
  // construction. Threading a `timezone` prop through ForecastTab would put one in
  // a component that has no other use for it.
  const { currentOrganization } = useData();

  const {
    goal,
    goalAmount,
    effectiveGoal,
    setLiveGoalInput,
    saveGoal,
    deleteGoal,
    funnelRates,
    bySource,
    monthMetrics,
    activeSources,
    toggleSource,
    inputOverrides,
    setInputOverride,
    clearOverride,
    rateOverrides,
    setRateOverride,
    clearRateOverride,
    reverseCalc,
    forwardProjection,
    paceStatus,
    paceProjected,
    isLoading,
  } = useRevenuePlanner(organizationId, currentMonthMetrics);

  // DM engagement analytics for current month — feeds the DM forward SourceCard
  const dmCurrentMonthRange = useMemo(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: now,
    };
  }, []);
  // CLAUDE.md #2 (TIMEZONE): the third arg is load-bearing. useDMAnalytics falls back
  // to UTC when it is omitted, silently shifting the month's day boundaries for any
  // org east of UTC (Rayston is Asia/Singapore). Optional param + silent fallback =
  // no type error, no runtime error, just wrong buckets. Regression from `9af22b60`.
  const { data: dmAnalyticsData } = useDMAnalytics(
    organizationId ?? null,
    dmCurrentMonthRange,
    currentOrganization?.timezone ?? "UTC"
  );

  const goalMonth = goal?.month;
  const monthLabel = useMemo(() => {
    if (goalMonth) {
      return new Date(goalMonth).toLocaleString("default", { month: "long", year: "numeric" });
    }
    const now = new Date();
    return now.toLocaleString("default", { month: "long", year: "numeric" });
  }, [goalMonth]);

  // Compute default input for each source (used as slider default).
  // Webinar: total registrants this month (not per-day). Others: leads/day.
  const defaultLeads = useMemo(() => {
    const result: Record<SourceKey, number> = { webinar: 0, typeform: 0, dm: 0 };
    for (const key of SOURCE_KEYS) {
      const rates = funnelRates[key];
      if (!rates) continue;
      if (key === "webinar") {
        result[key] = rates.total_signups ?? rates.leads_in;
      } else if (monthMetrics.days_elapsed > 0) {
        result[key] = Math.ceil(rates.leads_in / monthMetrics.days_elapsed);
      }
    }
    return result;
  }, [funnelRates, monthMetrics.days_elapsed]);

  if (isLoading) {
    return (
      <div
        className="liquid-glass rounded-[14px] flex items-center justify-center"
        style={{ padding: "60px", minHeight: 300 }}
      >
        <span className="text-glass-muted text-sm">Loading planner...</span>
      </div>
    );
  }

  // Check if we have any funnel data
  const hasData = SOURCE_KEYS.some((k) => funnelRates[k]);

  if (!hasData) {
    return (
      <div
        className="liquid-glass rounded-[14px] flex flex-col items-center justify-center gap-2"
        style={{ padding: "60px", minHeight: 200 }}
      >
        <span className="text-glass-muted text-sm">No funnel data available for this period.</span>
        <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Funnel metrics are needed to calculate projections.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Goal Header */}
      <GoalHeader
        goalAmount={goalAmount}
        cashSoFar={monthMetrics.cash_so_far}
        paceStatus={paceStatus}
        paceProjected={paceProjected}
        monthLabel={monthLabel}
        onSave={saveGoal}
        onDelete={deleteGoal}
        onInputChange={setLiveGoalInput}
        canEdit={canEdit}
      />

      {/* Source toggles — always visible when funnel data exists */}
      <SourceToggle
        activeSources={activeSources}
        onToggle={canEdit ? toggleSource : () => {}}
        disabled={!canEdit}
      />

      {/* Two-Panel Layout — Row-based for horizontal alignment */}
      <div className="space-y-4">
        {/* Column Headers with descriptions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h3
              className="font-semibold tracking-wide uppercase"
              style={{
                fontSize: 11,
                color: "var(--text-secondary)",
                letterSpacing: "0.08em",
              }}
            >
              What We Need
            </h3>
            <p
              style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2, lineHeight: 1.4 }}
            >
              Based on your current conversion rates, this is how many leads each source needs daily
              to hit your cash goal
            </p>
          </div>
          <div>
            <h3
              className="font-semibold tracking-wide uppercase"
              style={{
                fontSize: 11,
                color: "var(--text-secondary)",
                letterSpacing: "0.08em",
              }}
            >
              What Happens If...
            </h3>
            <p
              style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2, lineHeight: 1.4 }}
            >
              Move the slider to test scenarios. "What if we got 20 more webinar leads per day?" See
              the projected cash by end of month instantly.
            </p>
          </div>
        </div>

        {/* Source Cards — Each source is a row with aligned left/right cards */}
        {SOURCE_KEYS.map((key) => {
          const rev = reverseCalc[key];
          const rates = funnelRates[key];
          const fwd = forwardProjection.bySource[key];
          const currentLeads = inputOverrides[key] ?? defaultLeads[key];
          const currentRateOverrides = rateOverrides[key];
          // Webinar slider max based on historical signups (generous headroom)
          const webinarSliderMax =
            key === "webinar"
              ? Math.max(500, Math.ceil((rates?.total_signups ?? rates?.leads_in ?? 200) * 3))
              : undefined;

          return (
            <div key={key} className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
              {/* LEFT: What We Need */}
              {effectiveGoal > 0 ? (
                <SourceCard
                  variant="reverse"
                  sourceKey={key}
                  active={activeSources.includes(key)}
                  sourceGoal={rev.source_goal}
                  sourceShare={rev.source_share}
                  neededLeadsPerDay={rev.needed_leads_per_day}
                  actualLeadsPerDay={rev.actual_leads_per_day}
                  gapLeadsPerDay={rev.gap_leads_per_day}
                  bookRate={rates?.book_rate ?? 0}
                  showRate={rates?.show_rate ?? 0}
                  closeRate={rates?.close_rate ?? 0}
                  cashEarned={rates?.cash ?? 0}
                  rawSourceData={bySource[key]}
                />
              ) : (
                <div
                  className="rounded-[14px] border border-dashed flex items-center justify-center"
                  style={{
                    padding: "16px",
                    minHeight: 180,
                    borderColor: "var(--border)",
                    background: "var(--bg)",
                  }}
                >
                  <span
                    style={{ fontSize: 11, color: "var(--text-tertiary)", textAlign: "center" }}
                  >
                    Set a goal to see
                    <br />
                    required daily inputs
                  </span>
                </div>
              )}

              {/* RIGHT: What Happens If */}
              <SourceCard
                variant="forward"
                sourceKey={key}
                active={activeSources.includes(key)}
                leadsPerDay={currentLeads}
                onLeadsChange={(v) => setInputOverride(key, v)}
                onReset={() => {
                  clearOverride(key);
                  clearRateOverride(key);
                }}
                isOverridden={key in inputOverrides || key in rateOverrides}
                projectedLeads={fwd.projected_leads}
                projectedAttended={fwd.projected_attended}
                projectedBooked={fwd.projected_booked}
                projectedShown={fwd.projected_shown}
                projectedClosed={fwd.projected_closed}
                projectedCash={fwd.projected_cash}
                rateOverrides={currentRateOverrides}
                historicalRates={
                  rates
                    ? {
                        book_rate: rates.book_rate,
                        show_rate: rates.show_rate,
                        close_rate: rates.close_rate,
                        aov: rates.aov,
                        attendance_rate: rates.attendance_rate,
                      }
                    : undefined
                }
                onRateOverride={
                  canEdit
                    ? (field: keyof RateOverrides, value: number) =>
                        setRateOverride(key, field, value)
                    : undefined
                }
                onRateOverrideClear={
                  canEdit
                    ? (field: keyof RateOverrides) => clearRateOverride(key, field)
                    : undefined
                }
                webinarSliderMax={webinarSliderMax}
                dmAnalytics={
                  key === "dm"
                    ? {
                        newDMChats: dmAnalyticsData.totalNewDMs,
                        responseRate: dmAnalyticsData.responseRate,
                        realConvos: dmAnalyticsData.totalDeepConvos,
                        callsPitched: bySource["dm"]?.booked ?? 0,
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>

      {/* Projection Summary — shows when there's an effective goal */}
      {effectiveGoal > 0 && (
        <ProjectionSummary
          goalAmount={effectiveGoal}
          projection={forwardProjection}
          paceProjected={paceProjected}
          paceStatus={paceStatus}
        />
      )}
    </div>
  );
}
